
import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { formatCurrency } from "../utils/formatters";
import { TrendingUp, TrendingDown, Building2 } from "lucide-react";
import { random } from "lodash";

type RelatedPartyTransaction = {
  name: string;
  legal_name: string;
  relationship: string;
  type_of_transaction: "Revenue" | "Expense";
  amount: number;
  cin?: string;
  din?: string;
};

type RelatedPartyTransactionYear = {
  financial_year: string;
  company: RelatedPartyTransaction[];
  llp: RelatedPartyTransaction[];
  individual: RelatedPartyTransaction[];
  others: RelatedPartyTransaction[];
};

type RelatedPartyTransactionsProps = {
  transactions: RelatedPartyTransactionYear[];
};

const RelatedPartyTransactionsSection: React.FC<RelatedPartyTransactionsProps> = ({
  transactions,
}) => {
  if (!transactions || transactions.length === 0) {
    return (
      <Card className="bg-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Related Party Transactions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-500 text-center py-8">
          This company has no reported related party transactions.
          </p>
        </CardContent>
      </Card>
    );
  }

  const recentTransactions = [...transactions]
  .sort((a, b) => parseInt(b.financial_year) - parseInt(a.financial_year))
  .slice(0, 3);

  const processedData = recentTransactions.map((yearData) => {
    const allTransactions = [
      ...yearData.company,
      ...yearData.llp,
      ...yearData.individual,
      ...yearData.others
    ];
  
    const groupedTransactions = allTransactions.reduce((acc, txn) => {
      const key = txn.cin || txn.din || txn.name;
      const rawAmount = txn.amount;
      const amount = typeof rawAmount === "number" ? rawAmount : "-";
  
      if (!acc[key]) {
        acc[key] = {
          name: txn.name,
          legal_name: txn.legal_name,
          id: txn.cin || txn.din || txn.name,
          cin: txn.cin,
          din: txn.din,
          relationship: txn.relationship,
          totalRevenue: 0,
          totalExpense: 0,
          revenueDisplay: [],
          expenseDisplay: []
        };
      }
  
      if (txn.type_of_transaction === "Revenue") {
        if (amount !== "-") acc[key].totalRevenue += amount;
        acc[key].revenueDisplay.push(amount);
      } else {
        if (amount !== "-") acc[key].totalExpense += amount;
        acc[key].expenseDisplay.push(amount);
      }
  
      return acc;
    }, {} as Record<string, any>);
  
    const entitiesArray = Object.values(groupedTransactions).filter(
      (e: any) => e.totalRevenue !== 0 || e.totalExpense !== 0
    );
  
    const yearTotalRevenue = entitiesArray.reduce((sum: number, e: any) => sum + e.totalRevenue, 0);
    const yearTotalExpense = entitiesArray.reduce((sum: number, e: any) => sum + e.totalExpense, 0);
  
    // Calculate net amount safely
    let netAmount = 0;
    if (yearTotalRevenue && !yearTotalExpense) {
      netAmount = yearTotalRevenue;
    } else if (!yearTotalRevenue && yearTotalExpense) {
      netAmount = -yearTotalExpense;
    } else if (yearTotalRevenue && yearTotalExpense) {
      netAmount = yearTotalRevenue - yearTotalExpense;
    }
  
    return {
      year: yearData.financial_year,
      entities: entitiesArray.map(entity => ({
        ...entity,
        totalRevenue: entity.totalRevenue || "-",
        totalExpense: entity.totalExpense || "-"
      })),
      totalRevenue: yearTotalRevenue || "-",
      totalExpense: yearTotalExpense || "-",
      netAmount
    };
  });
  


  const renderYearSection = (data: any) => (
    <> 
    {data.entities.length > 0 && (<div key={data.year} className="mb-8">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          FY {data.year.substring(0, 4)}
        </h3>
      </div>
      
      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="font-semibold">Company Details</TableHead>
              <TableHead className="font-semibold">Relationship</TableHead>
              <TableHead className="font-semibold text-center">Revenue</TableHead>
              <TableHead className="font-semibold text-center">Expense</TableHead>
              <TableHead className="font-semibold text-center">Net Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            
                {data.entities.map((entity: any, index: number) => {
                  const totalRevenue = typeof entity.totalRevenue === 'number' ? entity.totalRevenue : 0;
                  const totalExpense = typeof entity.totalExpense === 'number' ? entity.totalExpense : 0;
                  const netAmount = totalRevenue - totalExpense;
                  return (
                    <TableRow key={entity.id + "_" + Math.floor(Math.random() * 10000)} className="hover:bg-gray-50 transition-colors">
                      <TableCell>
                        <div>
                          <div className="font-medium text-gray-900">{entity.name}</div>
                          <div className="text-sm text-gray-500">{entity.cin ? `CIN : ${entity.id}` : `DIN : ${entity.id}`}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          {entity.relationship}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {entity.totalRevenue > 0 ? (
                          <div className="flex items-center justify-center gap-1 text-green-700">
                            <TrendingUp className="h-4 w-4" />
                            {formatCurrency(entity.totalRevenue)}
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                          {entity.totalExpense > 0 ? (
                          <div className="flex items-center justify-center gap-1 text-red-700">
                            <TrendingDown className="h-4 w-4" />
                            {formatCurrency(entity.totalExpense)}
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {/* {netAmount} */}
                        <div className={`font-medium ${netAmount >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {formatCurrency(Math.abs(netAmount))}
                          {netAmount < 0 && <span className="text-xs ml-1">(net expense)</span>}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Total Row */}
                <TableRow className="bg-blue-50 border-t-2 border-blue-200 font-semibold">
                  <TableCell className="font-bold">Total</TableCell>
                  <TableCell></TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1 text-green-700 font-bold">
                      <TrendingUp className="h-4 w-4" />
                      {formatCurrency(data.totalRevenue)}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1 text-red-700 font-bold">
                      <TrendingDown className="h-4 w-4" />
                      {formatCurrency(data.totalExpense)}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className={`font-bold ${data.netAmount >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatCurrency(Math.abs(data.netAmount))}
                      {data.netAmount < 0 && <span className="text-xs ml-1">(net expense)</span>}
                    </div>
                  </TableCell>
                </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>) }   
    </>
  );
  const hasSections = processedData.some(d => Array.isArray(d.entities) && d.entities.length > 0);
  return (
    <>
      {hasSections ? (
        <Card className="w-full mt-4 bg-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              Related Party Transactions
            </CardTitle>
          </CardHeader>
          <CardContent>
            {processedData.map(renderYearSection)}
          </CardContent>
        </Card>
      ) : (

        <Card className="w-full mt-4 bg-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              Related Party Transactions
            </CardTitle>
          </CardHeader>
          <CardContent>
          This company has no reported related party transactions.
          </CardContent>
        </Card>

        // 3. Show fallback message if nothing to show
       
      )}
    </>
  );
};

export default RelatedPartyTransactionsSection;
